import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-for-focus",
  title: "A prayer for focus — when your attention keeps leaving",
  h1: "A prayer for focus",
  description:
    "A prayer for work you cannot start, with four passages on attention and a straight answer about what prayer will not do to a fragmented afternoon.",
  targets: [
    "prayer for focus",
    "prayer for concentration",
    "prayer before studying",
    "prayer to stop being distracted",
  ],
  published: "2026-09-03",
  standfirst:
    "Attention is the one thing you spend that you cannot get back, and most of it goes without a decision ever being made.",
  takeaways: [
    'Ask for one task, not for a personality. "Let me finish this one thing" is a request; "make me a focused person" is a wish.',
    "Psalm 119:37 asks for the eyes to be turned away — an admission that looking is the mechanism, and that the fix is upstream of willpower.",
    "Prayer will not undo a workspace built to interrupt you. Close the tabs first; the prayer is for the part that is left.",
  ],
  when: [
    "Before you start, while the file is open and you have not typed anything.",
    "After the fourth time you have picked up the phone without deciding to.",
    "When the work is not hard, only unpleasant, and you are looking for a reason to postpone it.",
    "In the twenty minutes before a deadline that you are spending on anything else.",
  ],
  prayer: [
    "God, my attention is all over the place and I have been pretending that is out of my control. It is partly out of my control. It is not entirely.",
    "Here is the one thing I need to do. Not everything — this. Give me the length of it.",
    "When I reach for the phone in the next hour, let me notice my hand moving before it gets there.",
    "And take the fear off this if the fear is what is making me avoid it. I would rather do it badly than keep not starting it.",
    "Whatever is actually urgent today, put it in front of me. Let the rest wait until I have finished one thing.",
  ],
  short:
    "God, one thing, for as long as it takes. Let me notice my hand reaching.",
  honest: [
    "Praying this will not make an interrupt-driven job into a quiet one. If your work requires you to be reachable every four minutes, the problem is the arrangement and no amount of asking will change what the arrangement is doing to you.",
    "It also does not diagnose anything. Attention that has never worked, in any setting, at any age, is worth asking a doctor about rather than praying harder about.",
    "And focus is not a virtue. It is a capacity, useful for good work and equally useful for bad. The prayer is for the work, not for the feeling of being disciplined.",
  ],
  verses: [
    {
      verse: {
        text: "Turn my eyes away from looking at worthless things. Revive me in your ways.",
        ref: "Psalm 119:37",
      },
      note: 'Notice who is asked to do the turning. The psalmist does not say "help me look away"; he asks to be turned, which is an admission that by the time the looking has started he is not the one steering. The second half is the tell: what is asked for is not less looking but being brought back to life, because the worthless things are absorbing precisely the attention that living takes.',
    },
    {
      verse: {
        text: "Let your eyes look straight ahead. Fix your gaze directly before you.",
        ref: "Proverbs 4:25",
      },
      note: "Part of a passage that works down the body — the heart, then the mouth, then the eyes, then the feet — and it is the eyes that get the instruction about direction. Ancient wisdom about walking a road, and still the most concrete description of what a distracted afternoon feels like: not a choice to go elsewhere, just a gaze that keeps drifting off the path.",
    },
    {
      verse: {
        text: "Set your mind on the things that are above, not on the things that are on the earth.",
        ref: "Colossians 3:2",
      },
      note: 'Regularly used to mean "think about heaven instead of your job", which the surrounding chapter contradicts immediately: the next twenty verses are about anger, speech, work and family, all of them extremely earthbound. The instruction is about what you are oriented by, not about withdrawing from the desk.',
    },
    {
      verse: {
        text: "The lamp of the body is the eye. If therefore your eye is sound, your whole body will be full of light.",
        ref: "Matthew 6:22",
      },
      note: "Sitting in the Sermon on the Mount between a passage about treasure and a passage about serving two masters, which is the context that makes it land: this is about what you keep looking at determining what you become, and it is bracketed by money on one side and divided loyalty on the other. A remarkably accurate description of a feed.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Ask for the task, not the trait",
      id: "ask-for-the-task-not-the-trait",
    },
    {
      type: "p",
      text: 'Prayers for focus usually fail because of their size. "Make me disciplined" has no edges, no end and no way of being answered visibly, so nothing about the afternoon changes and the conclusion drawn is about God rather than about the request.',
    },
    {
      type: "p",
      text: "One task, one length of time. That is small enough to be answered and small enough for you to notice when it is.",
    },
    {
      type: "h2",
      text: "Close the tabs before you pray",
      id: "close-the-tabs-before-you-pray",
    },
    {
      type: "p",
      text: "There is a version of praying about distraction that is itself a distraction: a spiritual-feeling activity performed instead of the plainly effective one. Shut the tabs, put {the phone} in a drawer, and then pray about the remaining difficulty, which will be real and will be about you.",
      links: {
        "the phone": "/blog/dopamine-detox-christian",
      },
    },
    {
      type: "note",
      text: "The measure of whether the arrangement is doing its job: can you undo it with one tap? If yes, it is a suggestion rather than a boundary, and your attention will treat it as one.",
    },
    {
      type: "h2",
      text: "Noticing is most of it",
      id: "noticing-is-most-of-it",
    },
    {
      type: "p",
      text: "The request in the prayer is not to stop reaching for the phone. It is to notice the reaching. Almost all of it happens below the level of decision — hand moves, thumb finds the app, four minutes gone — and a habit you have not noticed is one you cannot address.",
    },
    {
      type: "p",
      text: "Answered prayer here looks unimpressive from the outside. It is catching yourself mid-reach and putting the hand back down, three or four times an hour, until the reaching gets rarer.",
    },
  ],
  faq: [
    {
      q: "What can I pray before studying?",
      a: 'Name the single task and ask for its length: "God, this one thing, for as long as it takes." Then close everything that is not it — the prayer is for the difficulty that remains after that, which is the real one.',
    },
    {
      q: "Is there a Bible verse about distraction?",
      a: "Psalm 119:37 is the closest thing to one: turn my eyes away from looking at worthless things. Proverbs 4:25 gives the same instruction in the positive — fix your gaze directly before you.",
    },
    {
      q: "Does praying actually help concentration?",
      a: "It helps with intention and with noticing, which are two of the three parts. It does not do anything about an environment engineered to interrupt you, and expecting it to is how people conclude prayer does not work.",
    },
  ],
  relatedPrayers: [
    "prayer-for-self-control",
    "morning-prayer",
    "prayer-before-an-exam",
  ],
  related: ["dopamine-detox-christian", "screen-time-limits-dont-work"],
  relatedTopics: ["bible-verses-about-time", "bible-verses-about-self-control"],
  relatedPassages: ["romans-12-2"],
};
